import {useCallback, useEffect, useMemo, useState} from 'react';
import {Formik} from 'formik';
import {useHistory, useLocation} from 'react-router-dom';
import {addSurvey} from '../../../api/surveys';
import Container from '../../common/layout/Container';
import Spinner from '../../common/Spinner';
import AddSurveyForm from './Form';
import Error from '../../common/Error';
import useFetch from '../../../hooks/useFetch';
import {setTitle} from '../../../store/header/header.slice';
import {useAppDispatch} from '../../../store/hooks';
import {mapAvailablePatients} from '../helpers';
import routes from '../routes';
import {addSurveyValidationSchema} from '../../../constants/validationSchemes';
import {NewSurveyFormData, PatientOptions, PatientsCallSheets} from '../types';
import {useTranslation} from 'react-i18next';
import {useSnackbarHook} from '../../../hooks/useSnackbar';

type LocationState = {
  state: {
    initialPatientId?: Nullable<string>,
    initialCallSheetId?: Nullable<string>
  }
}

const AddSurvey = () => {
  const {t} = useTranslation('surveys');
  const {state}: LocationState = useLocation();
  const {initialPatientId, initialCallSheetId} = state || {};
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [patientsOptions, setPatientsOptions] = useState<PatientOptions>([]);
  const [patientsCallSheets, setPatientsCallSheets] =
    useState<PatientsCallSheets>({});
  const {addSuccessToast, addErrorToast} = useSnackbarHook();

  const [
    availablePatients,
    isAvailablePatientsLoading,
    availablePatientsError
  ] = useFetch('/patients/available-for-survey');

  useEffect(() => {
    dispatch(setTitle('Surveys'));
  }, []);

  useEffect(() => {
    const {
      patientsCallSheets,
      patientOptions
    } = mapAvailablePatients(availablePatients);

    setPatientsOptions(patientOptions);
    setPatientsCallSheets(patientsCallSheets);
  }, [availablePatients]);

  const backToSurveyList = useCallback(() => history.push(routes.list()), []);

  const onSubmit = useCallback(async (values: NewSurveyFormData) => {
    const {patientName, ...sendData} = values;
    try {
      await addSurvey(sendData);
      addSuccessToast(t('toasts.addSurveyMessage', {patientName}));
      backToSurveyList();
    } catch (e) {
      addErrorToast(e?.message);
    }
  }, [backToSurveyList]);

  const initialValues = useMemo(() => ({
    patientId: initialPatientId || '',
    callSheetId: initialCallSheetId || '',
    patientName: initialPatientId ? patientsOptions?.find(({value}) => value === initialPatientId)?.fullName || '' : ''
  }), [initialPatientId, initialCallSheetId, patientsOptions]);

  if (isAvailablePatientsLoading) {
    return <Spinner loadingText="Getting available patients" />;
  }

  if (availablePatientsError) {
    return <Error message={availablePatientsError?.message} />;
  }

  return (
    <Container>
      <h2 className="subheader margin-top-25">Create New Survey</h2>
      <Formik
        initialValues={initialValues}
        validationSchema={addSurveyValidationSchema}
        enableReinitialize
        validateOnBlur={false}
        onSubmit={onSubmit}
      >
        {(formikProps) => (
          <AddSurveyForm
            formikProps={formikProps}
            patientsOptions={patientsOptions}
            patientsCallSheets={patientsCallSheets}
            backToSurveyList={backToSurveyList}
          />
        )}
      </Formik>
    </Container>
  );
};

export default AddSurvey;
